import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import axios from 'axios'
import Breadcrumbs from '../components/Breadcrumbs'

export default function Support() {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  })
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('sending')
    try {
      await axios.post('/api/support', formData)
      setStatus('success')
      setFormData({ name: '', email: '', subject: '', message: '' })
      setTimeout(() => setStatus('idle'), 6000)
    } catch {
      setStatus('error')
      setTimeout(() => setStatus('idle'), 5000)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  return (
    <div className="min-h-screen pb-24">
      <Breadcrumbs items={[{ key: 'support' }]} />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 text-primary mb-6">
            <span className="material-icons text-3xl">support_agent</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">{t('support.title')}</h1>
          <p className="text-slate-600 dark:text-slate-400">{t('support.subtitle')}</p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          onSubmit={handleSubmit}
          className="bg-white dark:bg-slate-800 rounded-2xl p-6 sm:p-8 shadow-xl border border-slate-200 dark:border-slate-700"
        >
          <div className="space-y-5">
            <div>
              <label htmlFor="support-name" className="block text-sm font-semibold mb-1.5">
                {t('support.form.name')} *
              </label>
              <input
                type="text"
                id="support-name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              />
            </div>
            <div>
              <label htmlFor="support-email" className="block text-sm font-semibold mb-1.5">
                {t('support.form.email')} *
              </label>
              <input
                type="email"
                id="support-email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              />
            </div>
            <div>
              <label htmlFor="support-subject" className="block text-sm font-semibold mb-1.5">
                {t('support.form.subject')} *
              </label>
              <input
                type="text"
                id="support-subject"
                name="subject"
                required
                value={formData.subject}
                onChange={handleChange}
                placeholder={t('support.form.subjectPlaceholder')}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-primary focus:border-transparent transition-all placeholder:text-slate-400"
              />
            </div>
            <div>
              <label htmlFor="support-message" className="block text-sm font-semibold mb-1.5">
                {t('support.form.message')} *
              </label>
              <textarea
                id="support-message"
                name="message"
                required
                rows={5}
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={status === 'sending'}
            className="mt-6 w-full px-6 py-4 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === 'sending' ? t('support.form.sending') : t('support.form.submit')}
          </button>

          {status === 'success' && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 text-center text-green-600 dark:text-green-400 text-sm font-medium"
            >
              {t('support.form.success')}
            </motion.p>
          )}
          {status === 'error' && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 text-center text-red-600 dark:text-red-400 text-sm font-medium"
            >
              {t('support.form.error')}
            </motion.p>
          )}
        </motion.form>
      </div>
    </div>
  )
}
